import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useGrns, useGrnItemsTotals } from "@/features/grns/api";
import { GrnKindBadge } from "@/features/grns/badges";
import { GRN_KINDS } from "@/features/grns/types";
import { ActionGuard } from "@/components/auth/ActionGuard";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { useModuleAccess } from "@/hooks/useIsReadOnly";
import { apiList } from "@/integrations/api/client";
import { ListExportButtons } from "@/features/reports/ListExportButtons";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { canExportGrnReports } from "@/lib/roles";
import { useBranch } from "@/context/BranchContext";

export const Route = createFileRoute("/_authenticated/grns/")({
  component: GrnIndex,
});

function GrnIndex() {
  const navigate = useNavigate();
  const { canWrite } = useModuleAccess("grns");
  const { data: me } = useCurrentUser();
  const canExportGrn = canExportGrnReports(me?.roles);
  const [search, setSearch] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [kind, setKind] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { currentBranchId, lockedBranchId } = useBranch();
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const { data: rows = [], isLoading } = useGrns({
    search,
    kind: kind === "all" ? null : kind,
    poNumber,
    from: from || undefined,
    to: to || undefined,
    branchId: effectiveBranchId,
  });

  const ids = useMemo(() => rows.map((r) => r.id), [rows]);
  const { data: totals } = useGrnItemsTotals(ids);

  const assetGrnIds = useMemo(
    () => rows.filter((r) => r.kind === "asset").map((r) => r.id),
    [rows],
  );
  const { data: linkedAssetGrnCount = new Map<string, number>() } = useQuery({
    queryKey: ["grns_with_assets_count", assetGrnIds],
    enabled: assetGrnIds.length > 0,
    queryFn: async () => {
      try {
        const res = await apiList<{ source_grn?: string; source_grn_id?: string }>("/api/v1/assets/", { source_grn_id_in: assetGrnIds.join(","), limit: 10000 });
        const map = new Map<string, number>();
        for (const row of (res.results || [])) {
          const grnId = row.source_grn || row.source_grn_id;
          if (grnId) {
            map.set(grnId, (map.get(grnId) || 0) + 1);
          }
        }
        return map;
      } catch (e) {
        return new Map<string, number>();
      }
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Goods Receipt Notes</h1> <ReadOnlyPill module="grns" />
          <p className="text-sm text-muted-foreground">Record receipts for spares, assets and consumables.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ListExportButtons
            rows={() =>
              rows.map((r) => {
                const t = totals?.get(r.id);
                return {
                  "GRN No": r.grn_no,
                  Kind: r.kind,
                  "PO Number": r.po_number ?? "",
                  Vendor: r.vendor?.name ?? "",
                  Branch: r.branch?.name ?? "",
                  Invoice: r.invoice_no ?? "",
                  "Received Date": r.received_date ?? "",
                  "Received Qty": (r.items ?? []).reduce((a: number, i: { qty?: string | number }) => a + Number(i.qty ?? 0), 0),
                  "Accepted Qty": t?.accepted ?? "",
                  "Grand Total": t?.total ?? "",
                  "Asset Created": r.kind === "asset" ? (() => {
                    const createdCount = linkedAssetGrnCount.get(r.id) || 0;
                    const accepted = t?.accepted || 0;
                    if (createdCount === 0) return "No";
                    if (createdCount < accepted) return `Partial (${createdCount}/${accepted})`;
                    return "Yes";
                  })() : "",
                  "Legacy": r.is_legacy ? "Yes" : "",
                };
              })
            }
            filename="GRN_Register"
            sheetName="GRN Register"
            disabled={isLoading || rows.length === 0}
            canExport={canExportGrn}
          />
          <ActionGuard can={canWrite}>
            <Button onClick={() => navigate({ to: "/grns/new" })}><Plus className="mr-1 h-4 w-4" />New GRN</Button>
          </ActionGuard>
        </div>
      </div>

      <Card><CardContent className="p-3 flex flex-wrap items-center gap-2">
        <Input className="w-56" placeholder="Search GRN / Invoice…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Input className="w-40" placeholder="PO Number" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            {GRN_KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" className="w-40" value={from} onChange={(e) => setFrom(e.target.value)} />
        <Input type="date" className="w-40" value={to} onChange={(e) => setTo(e.target.value)} />
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
          rows.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No GRNs.</div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>GRN No</TableHead><TableHead>Kind</TableHead><TableHead>PO</TableHead>
                <TableHead>Vendor</TableHead><TableHead>Branch</TableHead><TableHead>Invoice</TableHead>
                <TableHead>Received</TableHead>
                <TableHead className="text-right">Accepted</TableHead>
                <TableHead className="text-right">Grand ₹</TableHead>
                <TableHead>Asset</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const t = totals?.get(r.id);
                  return (
                    <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate({ to: "/grns/$grnId", params: { grnId: r.id } })}>
                      <TableCell className="font-mono text-xs">
                        <Link to="/grns/$grnId" params={{ grnId: r.id }} className="text-primary hover:underline">{r.grn_no}</Link>
                        {r.is_legacy && <Badge variant="outline" className="ml-2 text-[10px]">legacy</Badge>}
                      </TableCell>
                      <TableCell><GrnKindBadge kind={r.kind as any} /></TableCell>
                      <TableCell className="font-mono text-xs">{r.po_number ?? "—"}</TableCell>
                      <TableCell className="text-sm">{r.vendor?.name ?? "—"}</TableCell>
                      <TableCell className="text-xs">{r.branch?.name ?? "—"}</TableCell>
                      <TableCell className="text-xs">{r.invoice_no ?? "—"}</TableCell>
                      <TableCell className="text-xs">{r.received_date}</TableCell>
                      <TableCell className="text-right tabular-nums">{t ? t.accepted : (r.items ?? []).reduce((a: any, i: any) => a + Number(i.qty ?? 0), 0)}</TableCell>
                      <TableCell className="text-right tabular-nums">{t ? t.total.toFixed(2) : "—"}</TableCell>
                      <TableCell>
                        {r.kind === "asset"
                          ? (() => {
                              const createdCount = linkedAssetGrnCount.get(r.id) || 0;
                              const accepted = t?.accepted || 0;
                              if (createdCount === 0) return <Badge variant="outline">Asset Pending</Badge>;
                              if (createdCount < accepted) return <Badge variant="default">Asset Partial ({createdCount}/{accepted})</Badge>;
                              return <Badge variant="secondary">Asset Created</Badge>;
                            })()
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
      </CardContent></Card>
    </div>
  );
}
